import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError, hasApiBaseUrl } from "../utils/api";

export type MeResponse = {
  user?: Record<string, unknown>;
  customer?: Record<string, unknown>;
};

type AuthContextValue = {
  me: MeResponse | null;
  loading: boolean;
  error?: string;
  loadMe: () => Promise<boolean>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const loadMe = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await apiFetch<MeResponse>("/me");
      setMe(data);
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setMe(null);
        return false;
      }
      setMe(null);
      setError("Login fehlgeschlagen. Bitte versuche es erneut.");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setMe(null);
    setError(undefined);
  }, []);

  useEffect(() => {
    if (!hasApiBaseUrl()) return;
    void loadMe();
  }, [loadMe]);

  const value = useMemo(
    () => ({ me, loading, error, loadMe, logout }),
    [me, loading, error, loadMe, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};
